import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { createPost } from '../../services/firebaseService';
import { MediaService } from '../../services/mediaService';
import { Post, Poll, MediaFile } from '../../types';
import { useUserProfile } from '../../hooks/useUser';
import GifPicker from '../UI/GifPicker';
import SchedulePost from '../UI/SchedulePost';
import MentionSelector from '../UI/MentionSelector';
import MediaSelector from '../UI/MediaSelector';
import MediaPreview from '../UI/MediaPreview';

interface CreatePostProps {
  onPostCreated: (post: Post) => void;
  category?: 'feedback' | 'meme' | 'research' | 'launchpad' | 'general' | 'dao' | 'annonces';
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

const CreatePost: React.FC<CreatePostProps> = ({ 
  onPostCreated, 
  category: forcedCategory, 
  isExpanded = false,
  onToggleExpand 
}) => {
  const { user, setUser } = useAuth();
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<'feedback' | 'meme' | 'research' | 'launchpad' | 'general' | 'dao' | 'annonces'>(forcedCategory ?? 'general');
  const [isPosting, setIsPosting] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaFile[]>([]);
  const [selectedGif, setSelectedGif] = useState<string>('');
  const [isGifPickerOpen, setIsGifPickerOpen] = useState(false);
  const [poll, setPoll] = useState<Poll | null>(null);
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState([
    { id: '1', text: '' },
    { id: '2', text: '' }
  ]);
  const [pollDuration, setPollDuration] = useState(24);
  const [allowMultipleChoice, setAllowMultipleChoice] = useState(false);
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [isSchedulePostOpen, setIsSchedulePostOpen] = useState(false);
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const [showMentionSelector, setShowMentionSelector] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [cursorPosition, setCursorPosition] = useState(0);
  
  // Thread functionality
  const [isThreadMode, setIsThreadMode] = useState(false);
  const [threadParts, setThreadParts] = useState<Array<{content: string; mediaUrls: string[]; selectedMedia: MediaFile[]; selectedGif: string}>>([]);
  const [currentThreadPart, setCurrentThreadPart] = useState(0);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { getUserById } = useUserProfile();
  const categories = [
    { value: 'annonces', label: 'Annonces', emoji: '📢' },
    { value: 'dao', label: 'DAO', emoji: '🏛️' },
    { value: 'general', label: 'Général', emoji: '🌟' },
    { value: 'feedback', label: 'Feedback', emoji: '💬' },
    { value: 'research', label: 'Research', emoji: '🔬' },
    { value: 'meme', label: 'Meme', emoji: '😂' },
    { value: 'launchpad', label: 'Launchpad', emoji: '🚀' },
  ];

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Ne pas permettre de sélectionner des images si un GIF ou un sondage est déjà sélectionné
    if (selectedGif || poll) {
      alert('Vous ne pouvez pas ajouter de médias si un GIF ou un sondage est déjà sélectionné.');
      return;
    }

    const files = Array.from(e.target.files || []);
    const maxFiles = 4;
    
    if (selectedMedia.length + files.length > maxFiles) {
      alert(`Vous ne pouvez sélectionner que ${maxFiles} fichiers maximum.`);
      return;
    }

    // Utiliser MediaSelector à la place - cette fonction est maintenant obsolète
    console.log('Utiliser le composant MediaSelector à la place');
  };

  const handleMediaSelect = async (mediaFiles: MediaFile[]) => {
    if (selectedGif || poll) {
      alert('Vous ne pouvez pas ajouter de médias si un GIF ou un sondage est déjà sélectionné.');
      MediaService.cleanupMediaFiles(mediaFiles);
      return;
    }

    setSelectedMedia(mediaFiles);
    
    // Forcer l'expansion du formulaire quand des médias sont ajoutés
    if (onToggleExpand && !isExpanded) {
      onToggleExpand();
    }
  };

  const removeMedia = (index: number) => {
    const mediaToRemove = selectedMedia[index];
    
    // Nettoyer les URLs de prévisualisation
    if (mediaToRemove.url) {
      URL.revokeObjectURL(mediaToRemove.url);
    }
    if (mediaToRemove.thumbnail) {
      URL.revokeObjectURL(mediaToRemove.thumbnail);
    }
    
    setSelectedMedia(prev => prev.filter((_, i) => i !== index));
  };

  const removeImage = (index: number) => {
    // Fonction obsolète - utiliser removeMedia à la place
    removeMedia(index);
  };

  const handleGifSelect = (gifUrl: string) => {
    setSelectedGif(gifUrl);
    // Supprimer les médias et sondages sélectionnés si un GIF est choisi
    MediaService.cleanupMediaFiles(selectedMedia);
    setSelectedMedia([]);
    setPoll(null);
    
    // Forcer l'expansion du formulaire quand un GIF est sélectionné
    if (onToggleExpand && !isExpanded) {
      onToggleExpand();
    }
  };

  const removeGif = () => {
    setSelectedGif('');
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    setContent(text);
    setCursorPosition(cursorPos);
    
    // Détecter les mentions (@)
    const beforeCursor = text.slice(0, cursorPos);
    const mentionMatch = beforeCursor.match(/@(\w*)$/);
    
    if (mentionMatch) {
      const query = mentionMatch[1];
      setMentionQuery(query);
      
      // Calculer la position du sélecteur de mentions
      if (textareaRef.current) {
        const textareaRect = textareaRef.current.getBoundingClientRect();
        const lineHeight = 24; // Hauteur approximative d'une ligne
        const lines = beforeCursor.split('\n').length;
        
        setMentionPosition({
          top: textareaRect.top + (lines * lineHeight) + 30,
          left: textareaRect.left + 10
        });
      }
      
      setShowMentionSelector(true);
    } else {
      setShowMentionSelector(false);
      setMentionQuery('');
    }
  };

  const handleMentionSelect = (user: { id: string; username: string; displayName: string }) => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const beforeCursor = content.slice(0, cursorPosition);
    const afterCursor = content.slice(cursorPosition);
    
    // Remplacer la mention partielle par la mention complète
    const mentionMatch = beforeCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      const beforeMention = beforeCursor.slice(0, mentionMatch.index);
      const newContent = `${beforeMention}@${user.username} ${afterCursor}`;
      
      setContent(newContent);
      setMentionedUsers(prev => [...new Set([...prev, user.id])]);
      setShowMentionSelector(false);
      
      // Repositionner le curseur après la mention
      setTimeout(() => {
        const newPosition = beforeMention.length + user.username.length + 2;
        textarea.setSelectionRange(newPosition, newPosition);
        textarea.focus();
      }, 0);
    }
  };

  const handleOpenPollCreator = () => {
    // Réinitialiser les champs du sondage
    setPollQuestion('');
    setPollOptions([{ id: '1', text: '' }, { id: '2', text: '' }]);
    setPollDuration(24);
    setAllowMultipleChoice(false);
    
    // Supprimer les médias si un sondage est créé
    MediaService.cleanupMediaFiles(selectedMedia);
    setSelectedMedia([]);
    setSelectedGif('');
    
    // Forcer l'expansion du formulaire et afficher le créateur
    if (onToggleExpand && !isExpanded) {
      onToggleExpand();
      setTimeout(() => {
        setShowPollCreator(true);
      }, 100);
    } else {
      setShowPollCreator(true);
    }
  };

  const handleOpenSchedulePost = () => {
    // Forcer l'expansion du formulaire avant d'ouvrir le modal
    if (onToggleExpand && !isExpanded) {
      onToggleExpand();
      // Petit délai pour que l'expansion se fasse avant l'ouverture du modal
      setTimeout(() => {
        setIsSchedulePostOpen(true);
      }, 100);
    } else {
      setIsSchedulePostOpen(true);
    }
  };

  const handleOpenGifPicker = () => {
    // Forcer l'expansion du formulaire avant d'ouvrir le picker
    if (onToggleExpand && !isExpanded) {
      onToggleExpand();
      // Petit délai pour que l'expansion se fasse avant l'ouverture du picker
      setTimeout(() => {
        setIsGifPickerOpen(true);
      }, 100);
    } else {
      setIsGifPickerOpen(true);
    }
  };

  const addPollOption = () => {
    if (pollOptions.length < 4) {
      setPollOptions([...pollOptions, { id: Date.now().toString(), text: '' }]);
    }
  };

  const removePollOption = (id: string) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter(opt => opt.id !== id));
    }
  };

  const updatePollOption = (id: string, text: string) => {
    setPollOptions(pollOptions.map(opt => opt.id === id ? { ...opt, text } : opt));
  };

  const handleCreatePoll = () => {
    if (pollQuestion.trim() && pollOptions.every(opt => opt.text.trim())) {
      const newPoll: Poll = {
        id: Date.now().toString(),
        question: pollQuestion.trim(),
        options: pollOptions.filter(opt => opt.text.trim()).map((opt: any) => ({
          ...opt,
          votes: []
        })),
        duration: pollDuration,
        allowMultipleChoice,
        createdAt: new Date(),
        expiresAt: new Date(Date.now() + pollDuration * 60 * 60 * 1000),
        totalVotes: 0,
        voters: []
      };
      
      setPoll(newPoll);
      setShowPollCreator(false);
      
      // Forcer l'expansion du formulaire quand un sondage est créé
      if (onToggleExpand && !isExpanded) {
        onToggleExpand();
      }
    }
  };

  const cancelPollCreation = () => {
    setPollQuestion('');
    setPollOptions([{ id: '1', text: '' }, { id: '2', text: '' }]);
    setPollDuration(24);
    setAllowMultipleChoice(false);
    setShowPollCreator(false);
  };

  const removePoll = () => {
    setPoll(null);
    setShowPollCreator(false);
  };

  const removeSchedule = () => {
    setScheduledDate(null);
  };

  const handleSchedule = (date: Date) => {
    setScheduledDate(date);
  };

  // Thread functions
  const enableThreadMode = () => {
    setIsThreadMode(true);
    if (onToggleExpand && !isExpanded) {
      onToggleExpand();
    }
  };

  const disableThreadMode = () => {
    setIsThreadMode(false);
    setThreadParts([]);
    setCurrentThreadPart(0);
  };

  const addThreadPart = () => {
    if (threadParts.length < 25) { // Limite de 25 parties + post principal = 26 total
      // Sauvegarder la partie actuelle si elle contient du contenu
      if (content.trim() || selectedMedia.length > 0 || selectedGif) {
        const currentPart = {
          content: content.trim(),
          mediaUrls: [],
          selectedMedia: [...selectedMedia],
          selectedGif: selectedGif
        };
        
        const newThreadParts = [...threadParts];
        newThreadParts[currentThreadPart] = currentPart;
        setThreadParts(newThreadParts);
      }
      
      // Créer une nouvelle partie
      setThreadParts(prev => [...prev, {
        content: '',
        mediaUrls: [],
        selectedMedia: [],
        selectedGif: ''
      }]);
      
      // Passer à la nouvelle partie
      setCurrentThreadPart(threadParts.length);
      
      // Réinitialiser les champs pour la nouvelle partie
      setContent('');
      MediaService.cleanupMediaFiles(selectedMedia);
      setSelectedMedia([]);
      setSelectedGif('');
    }
  };

  const removeThreadPart = (index: number) => {
    if (threadParts.length > 0) {
      const newThreadParts = threadParts.filter((_, i) => i !== index);
      setThreadParts(newThreadParts);
      
      // Ajuster l'index de la partie actuelle si nécessaire
      if (currentThreadPart > index) {
        setCurrentThreadPart(currentThreadPart - 1);
      } else if (currentThreadPart === index && index === threadParts.length - 1) {
        setCurrentThreadPart(Math.max(0, index - 1));
        // Charger la partie précédente
        if (index > 0) {
          const prevPart = newThreadParts[index - 1];
          setContent(prevPart.content);
          MediaService.cleanupMediaFiles(selectedMedia);
          setSelectedMedia([...prevPart.selectedMedia]);
          setSelectedGif(prevPart.selectedGif);
        }
      }
    }
  };

  const switchToThreadPart = (index: number) => {
    // Sauvegarder la partie actuelle
    if (content.trim() || selectedMedia.length > 0 || selectedGif) {
      const currentPart = {
        content: content.trim(),
        mediaUrls: [],
        selectedMedia: [...selectedMedia],
        selectedGif: selectedGif
      };
      
      const newThreadParts = [...threadParts];
      if (currentThreadPart === -1) {
        // On était sur le post principal, on ne sauvegarde pas ici
      } else {
        newThreadParts[currentThreadPart] = currentPart;
        setThreadParts(newThreadParts);
      }
    }
    
    // Charger la partie sélectionnée
    if (index === -1) {
      // Retour au post principal - on garde le contenu actuel
      setCurrentThreadPart(-1);
    } else if (index < threadParts.length) {
      const targetPart = threadParts[index];
      setContent(targetPart.content);
      // Nettoyer les anciens médias
      MediaService.cleanupMediaFiles(selectedMedia);
      setSelectedMedia([...targetPart.selectedMedia]);
      setSelectedGif(targetPart.selectedGif);
      setCurrentThreadPart(index);
    }
  };

  const uploadMedia = async (mediaFiles: MediaFile[]): Promise<{ urls: string[]; types: string[] }> => {
    try {
      if (mediaFiles.length === 0) return { urls: [], types: [] };
      
      console.log(`🚀 Upload de ${mediaFiles.length} fichier(s) média...`);
      const files = mediaFiles.map(mf => mf.file);
      const result = await MediaService.uploadPostMedia(files);
      console.log(`✅ Médias uploadés avec succès:`, result);
      
      return result;
    } catch (error) {
      console.error('❌ Erreur upload médias:', error);
      throw error;
    }
  };

  // Fonction pour nettoyer les données avant envoi à Firebase
  const cleanPostData = (data: any) => {
    const cleaned: any = {};
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        cleaned[key] = data[key];
      }
    });
    return cleaned;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || (!content.trim() && selectedMedia.length === 0 && !selectedGif && !poll && (!isThreadMode || threadParts.length === 0))) return;

    setIsPosting(true);
    try {
      // Si on est en mode thread, sauvegarder la partie actuelle
      let finalThreadParts = [...threadParts];
      if (isThreadMode && (content.trim() || selectedMedia.length > 0 || selectedGif)) {
        if (currentThreadPart === -1 || currentThreadPart >= threadParts.length) {
          // Ajouter une nouvelle partie
          finalThreadParts.push({
            content: content.trim(),
            mediaUrls: [],
            selectedMedia: [...selectedMedia],
            selectedGif: selectedGif
          });
        } else {
          // Mettre à jour la partie existante
          finalThreadParts[currentThreadPart] = {
            content: content.trim(),
            mediaUrls: [],
            selectedMedia: [...selectedMedia],
            selectedGif: selectedGif
          };
        }
      }

      // Upload des médias pour le post principal
      let mainMediaUrls: string[] = [];
      let mainMediaTypes: string[] = [];
      let mainContent = content.trim();
      
      // Si c'est un thread et que le contenu principal est vide, utiliser la première partie
      if (isThreadMode && !mainContent && finalThreadParts.length > 0) {
        mainContent = finalThreadParts[0].content;
        if (finalThreadParts[0].selectedGif) {
          mainMediaUrls = [finalThreadParts[0].selectedGif];
          mainMediaTypes = ['gif'];
        } else if (finalThreadParts[0].selectedMedia.length > 0) {
          const result = await uploadMedia(finalThreadParts[0].selectedMedia);
          mainMediaUrls = result.urls;
          mainMediaTypes = result.types;
        }
        // IMPORTANT: Supprimer la première partie des threadParts car elle devient le post principal
        finalThreadParts = finalThreadParts.slice(1);
      } else {
        // Upload normal pour le post principal
        if (selectedGif) {
          mainMediaUrls = [selectedGif];
          mainMediaTypes = ['gif'];
        } else if (selectedMedia.length > 0 && !poll) {
          const result = await uploadMedia(selectedMedia);
          mainMediaUrls = result.urls;
          mainMediaTypes = result.types;
        }
      }

      // Upload des médias pour chaque partie du thread
      if (isThreadMode) {
        for (let i = 0; i < finalThreadParts.length; i++) {
          const part = finalThreadParts[i];
          if (part.selectedGif) {
            part.mediaUrls = [part.selectedGif];
          } else if (part.selectedMedia.length > 0) {
            const result = await uploadMedia(part.selectedMedia);
            part.mediaUrls = result.urls;
          }
        }
      }

      // Préparation des données du post sans valeurs undefined
      const postData: any = {
        authorId: user.id,
        content: mainContent, // Utiliser mainContent au lieu de content.trim()
        mediaUrls: mainMediaUrls,
        mediaTypes: mainMediaTypes,
        links: [],
        category: category as any,
        likes: [],
        comments: [],
        shares: 0,
        views: 0,
        isDeleted: false,
        isPinned: false,
        tags: [],
        mentionedUsers,
        isScheduled: !!scheduledDate,
        // Thread properties
        isThread: isThreadMode,
        threadLength: isThreadMode ? finalThreadParts.length + 1 : undefined
      };

      // Ajouter seulement les champs qui ont des valeurs définies
      if (poll) {
        postData.poll = poll;
      }
      
      if (scheduledDate) {
        postData.scheduledFor = scheduledDate;
      }

      if (isThreadMode && finalThreadParts.length > 0) {
        postData.threadParts = finalThreadParts.map((part, index) => ({
          content: part.content,
          mediaUrls: part.mediaUrls,
          order: index + 1
        }));
      }

      // Nettoyer les données pour éviter les valeurs undefined
      const cleanedPostData = cleanPostData(postData);

      const postId = await createPost(cleanedPostData);

      // Ajout XP après création du post (non bloquant)
      try {
        const { XP_REWARDS } = await import('../../types');
        const { UserService } = await import('../../services/userService');
        await UserService.updateXP(user.id, XP_REWARDS.post);
        console.log('✅ XP mis à jour avec succès');
        // Attendre la propagation Firestore (500ms)
        await new Promise(res => setTimeout(res, 500));
        // Recharger le user depuis Firestore (pas le cache)
        const updatedUser = await getUserById(user.id);
        if (updatedUser) {
          setUser(updatedUser);
          console.log('👤 Profil utilisateur mis à jour');
        }
      } catch (err) {
        console.warn('⚠️ Erreur mise à jour XP (non critique):', err);
        // L'erreur XP ne doit pas empêcher le post d'être créé
      }

      // Créer l'objet post pour la mise à jour immédiate de l'UI
      const newPost: Post = {
        id: postId,
        authorId: user.id,
        content: mainContent, // Utiliser mainContent au lieu de content.trim()
        mediaUrls: mainMediaUrls,
        links: [],
        category: category as any,
        likes: [],
        comments: [],
        shares: 0,
        views: 0,
        isDeleted: false,
        isPinned: false,
        tags: [],
        mentionedUsers,
        poll: poll || undefined,
        isScheduled: !!scheduledDate,
        scheduledFor: scheduledDate || undefined,
        // Thread properties
        isThread: isThreadMode,
        threadId: isThreadMode ? postId : undefined,
        threadOrder: 0,
        threadParts: isThreadMode ? finalThreadParts.map((part, index) => ({
          id: `${postId}_part_${index + 1}`,
          content: part.content,
          mediaUrls: part.mediaUrls,
          mediaTypes: part.mediaUrls?.map(url => MediaService.getMediaTypeFromUrl(url)),
          order: index + 1,
          createdAt: new Date()
        })) : undefined,
        threadLength: isThreadMode ? finalThreadParts.length + 1 : undefined,
        createdAt: scheduledDate || new Date(),
        updatedAt: new Date()
      };

      onPostCreated(newPost);

      // Réinitialiser le formulaire
      setContent('');
      setCategory(forcedCategory ?? 'general');
      MediaService.cleanupMediaFiles(selectedMedia);
      setSelectedMedia([]);
      setSelectedGif('');
      setPoll(null);
      setShowPollCreator(false);
      setPollQuestion('');
      setPollOptions([{ id: '1', text: '' }, { id: '2', text: '' }]);
      setPollDuration(24);
      setAllowMultipleChoice(false);
      setScheduledDate(null);
      setMentionedUsers([]);
      // Reset thread states
      setIsThreadMode(false);
      setThreadParts([]);
      setCurrentThreadPart(0);
      
      // Réduire le formulaire après publication
      if (onToggleExpand && isExpanded) {
        onToggleExpand();
      }
    } catch (error) {
      console.error('Erreur lors de la création du post:', error);
      alert('Erreur lors de la publication du post');
    } finally {
      setIsPosting(false);
    }
  };

  const characterCount = content.length;
  const maxCharacters = isThreadMode ? 280 : 280; // Même limite par partie en mode thread
  const hasContent = content.trim() || selectedMedia.length > 0 || selectedGif || poll || (isThreadMode && threadParts.some(part => part.content.trim() || part.selectedMedia.length > 0 || part.selectedGif));

  return (
    <div className="cosmic-card p-6 mb-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* En-tête avec avatar utilisateur */}
        <div className="flex items-start space-x-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-emerald-700 to-cyan-600 flex items-center justify-center border border-cyan-400/30 animate-float">
            {user?.profilePicture ? (
              <img
                src={user.profilePicture}
                alt={user.displayName || 'User'}
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <span className="text-emerald-100 font-bold">
                {user?.displayName?.charAt(0) || 'U'}
              </span>
            )}
          </div>
          
          <div className="flex-1">
            {/* Zone de texte */}
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={content}
                onChange={handleTextChange}
                onFocus={() => onToggleExpand && !isExpanded && onToggleExpand()}
                placeholder="Quoi de neuf dans l'univers Jupiter ?"
                className="w-full bg-transparent border-none text-emerald-100 placeholder-emerald-300/60 resize-none focus:outline-none text-xl"
                rows={isExpanded ? 4 : 2}
                maxLength={maxCharacters}
                style={{ minHeight: isExpanded ? '120px' : '60px' }}
              />
              
              {/* Indicateur de caractères */}
              {isExpanded && (
                <div className="absolute bottom-2 right-2 text-xs text-emerald-300/60">
                  <span className={characterCount > maxCharacters * 0.9 ? 'text-red-400' : ''}>
                    {characterCount}/{maxCharacters}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contenu étendu avec animation de push */}
        <div className={`overflow-hidden transition-all duration-700 ease-out transform ${
          isExpanded 
            ? 'max-h-[800px] opacity-100 translate-y-0' 
            : 'max-h-0 opacity-0 -translate-y-4'
        }`}>
          <div className={`transition-all duration-500 ease-out ${
            isExpanded ? 'animate-gentleBounce' : ''
          }`}>
            {/* Prévisualisation du GIF */}
            {selectedGif && (
              <div className="mt-4 animate-slideInFromTop">
                <div className="relative inline-block rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-cosmic-neon">
                  <img
                    src={selectedGif}
                    alt="GIF sélectionné"
                    className="max-w-full max-h-64 object-contain rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={removeGif}
                    className="absolute top-2 right-2 bg-red-500/90 hover:bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-base transition-colors backdrop-blur-sm shadow-lg"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {/* Prévisualisation des médias */}
            {selectedMedia.length > 0 && (
              <MediaPreview
                mediaFiles={selectedMedia}
                onRemove={removeMedia}
                className="animate-slideInFromTop"
              />
            )}

            {/* Aperçu du sondage avec animation */}
            {(poll || showPollCreator) && (
              <div className="mt-4 animate-slideInFromTop">
                <div className="p-4 bg-emerald-900/20 border border-emerald-700/30 rounded-xl transform transition-all duration-500 hover:scale-[1.02]">
                  {poll ? (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-emerald-100">{poll.question}</h4>
                        <button
                          type="button"
                          onClick={removePoll}
                          className="text-red-400 hover:text-red-300 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    <div className="space-y-2">
                      {poll.options.map((option, index) => (
                        <div key={option.id} className="flex items-center space-x-2 text-emerald-200">
                          <div className="w-4 h-4 border border-emerald-600 rounded-sm"></div>
                          <span className="text-sm">{option.text}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 text-xs text-emerald-300/60">
                      Durée: {poll.duration}h • {poll.allowMultipleChoice ? 'Choix multiples' : 'Choix unique'}
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    {/* En-tête du créateur de sondage */}
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-emerald-100">Créer un sondage</h4>
                      <button
                        type="button"
                        onClick={cancelPollCreation}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Question du sondage */}
                    <div>
                      <label className="block text-sm font-medium text-emerald-200 mb-2">
                        Question
                      </label>
                      <input
                        type="text"
                        value={pollQuestion}
                        onChange={(e) => setPollQuestion(e.target.value)}
                        placeholder="Posez votre question..."
                        className="w-full bg-gray-800/70 border border-emerald-800/50 rounded-lg px-3 py-2 text-emerald-100 placeholder-emerald-300/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/70"
                        maxLength={120}
                      />
                      <div className="text-xs text-emerald-300/60 mt-1">
                        {pollQuestion.length}/120
                      </div>
                    </div>

                    {/* Options du sondage */}
                    <div>
                      <label className="block text-sm font-medium text-emerald-200 mb-2">
                        Options
                      </label>
                      <div className="space-y-2">
                        {pollOptions.map((option, index) => (
                          <div key={option.id} className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={option.text}
                              onChange={(e) => updatePollOption(option.id, e.target.value)}
                              placeholder={`Option ${index + 1}`}
                              className="flex-1 bg-gray-800/70 border border-emerald-800/50 rounded-lg px-3 py-2 text-emerald-100 placeholder-emerald-300/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/70"
                              maxLength={50}
                            />
                            {pollOptions.length > 2 && (
                              <button
                                type="button"
                                onClick={() => removePollOption(option.id)}
                                className="text-red-400 hover:text-red-300 transition-colors"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {pollOptions.length < 4 && (
                        <button
                          type="button"
                          onClick={addPollOption}
                          className="mt-2 text-cyan-300 hover:text-cyan-200 text-sm transition-colors"
                        >
                          + Ajouter une option
                        </button>
                      )}
                    </div>

                    {/* Durée du sondage */}
                    <div>
                      <label className="block text-sm font-medium text-emerald-200 mb-2">
                        Durée du sondage
                      </label>
                      <select
                        value={pollDuration}
                        onChange={(e) => setPollDuration(Number(e.target.value))}
                        className="w-full bg-gray-800/70 border border-emerald-800/50 rounded-lg px-3 py-2 text-emerald-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/70"
                      >
                        <option value={1}>1 heure</option>
                        <option value={6}>6 heures</option>
                        <option value={12}>12 heures</option>
                        <option value={24}>1 jour</option>
                        <option value={72}>3 jours</option>
                        <option value={168}>1 semaine</option>
                      </select>
                    </div>

                    {/* Choix multiple */}
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="multipleChoice"
                        checked={allowMultipleChoice}
                        onChange={(e) => setAllowMultipleChoice(e.target.checked)}
                        className="rounded border-emerald-800/50 bg-gray-800/70 text-cyan-400 focus:ring-cyan-400/70"
                      />
                      <label htmlFor="multipleChoice" className="text-sm text-emerald-200">
                        Autoriser les choix multiples
                      </label>
                    </div>

                    {/* Boutons d'action */}
                    <div className="flex justify-end space-x-3 pt-2">
                      <button
                        type="button"
                        onClick={cancelPollCreation}
                        className="px-4 py-2 text-emerald-300 hover:text-emerald-100 transition-colors"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={handleCreatePoll}
                        disabled={!pollQuestion.trim() || !pollOptions.every(opt => opt.text.trim())}
                        className="cosmic-button disabled:opacity-50"
                      >
                        Créer le sondage
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            )}

            {/* Programmation */}
            {scheduledDate && (
              <div className="mt-4 p-3 bg-cyan-900/20 border border-cyan-700/30 rounded-lg flex items-center justify-between">
                <div className="flex items-center space-x-2 text-cyan-200">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm">
                    Programmé pour le {scheduledDate.toLocaleString('fr-FR', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={removeSchedule}
                  className="text-red-400 hover:text-red-300 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

          {/* Interface Thread */}
          {isThreadMode && (
            <div className="mt-4 animate-slideInFromTop">
              <div className="p-4 bg-gradient-to-br from-emerald-900/20 via-cyan-900/20 to-emerald-800/20 border border-emerald-700/30 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h4 className="font-medium text-emerald-200">Mode Thread</h4>
                    <span className="text-xs bg-cyan-500/20 text-cyan-300 px-2 py-1 rounded-full">
                      {threadParts.length + 1} partie{threadParts.length + 1 > 1 ? 's' : ''}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={disableThreadMode}
                    className="text-red-400 hover:text-red-300 transition-colors"
                    title="Désactiver le mode thread"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Navigation entre les parties */}
                <div className="flex items-center space-x-2 mb-3 overflow-x-auto pb-2">
                  <button
                    onClick={() => switchToThreadPart(-1)}
                    className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                      currentThreadPart === -1
                        ? 'bg-cyan-500/30 text-cyan-200 border border-cyan-400/50'
                        : 'bg-gray-700/50 text-gray-300 border border-gray-600/50 hover:bg-cyan-500/20'
                    }`}
                  >
                    Post principal
                  </button>
                  {threadParts.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => switchToThreadPart(index)}
                      className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                        currentThreadPart === index
                          ? 'bg-cyan-500/30 text-cyan-200 border border-cyan-400/50'
                          : 'bg-gray-700/50 text-gray-300 border border-gray-600/50 hover:bg-cyan-500/20'
                      }`}
                    >
                      Partie {index + 1}
                      {threadParts[index]?.content.trim() && (
                        <span className="ml-1 text-green-400">✓</span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Actions du thread */}
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={addThreadPart}
                    disabled={threadParts.length >= 25}
                    className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm transition-colors ${
                      threadParts.length >= 25
                        ? 'text-gray-500 cursor-not-allowed'
                        : 'text-cyan-300 hover:text-cyan-200 hover:bg-cyan-900/20'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span>Ajouter une partie</span>
                  </button>

                  {currentThreadPart >= 0 && (
                    <button
                      type="button"
                      onClick={() => removeThreadPart(currentThreadPart)}
                      className="flex items-center space-x-1 px-3 py-1 rounded-lg text-sm text-red-400 hover:text-red-300 hover:bg-red-900/20 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Supprimer cette partie</span>
                    </button>
                  )}
                </div>

                <div className="mt-2 text-xs text-emerald-300/60">
                  Créez jusqu'à 25 parties pour dépasser la limite de 280 caractères. Chaque partie peut contenir du texte et des médias.
                </div>
              </div>
            </div>
          )}          </div>
        </div>

        {/* Sélecteur de catégorie - Toujours visible */}
        {!forcedCategory && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-emerald-200 mb-2">
              Catégorie
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as any)}
              className="w-full bg-gray-800/70 border border-emerald-800/50 rounded-lg px-3 py-2 text-emerald-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/70 backdrop-blur-sm"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value} className="bg-gray-900 text-emerald-100">
                  {cat.emoji} {cat.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-emerald-800/30">
          <div className="flex items-center space-x-1">
            {/* Sélecteur de médias */}
            <MediaSelector
              onMediaSelect={handleMediaSelect}
              maxFiles={4}
              disabled={!!selectedGif || !!poll}
              acceptVideo={true}
            />

            {/* Bouton GIF */}
            <button
              type="button"
              onClick={handleOpenGifPicker}
              disabled={selectedMedia.length > 0 || !!poll}
              className={`p-2 rounded-lg transition-colors cosmic-hover-effect ${selectedMedia.length > 0 || poll ? 'text-gray-500 cursor-not-allowed' : 'text-cyan-300 hover:text-cyan-200 hover:bg-emerald-800/20'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V1h-1m1 3h4a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2h4m0-3v3m0 0h8" />
              </svg>
            </button>

            {/* Bouton Sondage */}
            <button
              type="button"
              onClick={handleOpenPollCreator}
              disabled={selectedMedia.length > 0 || !!selectedGif}
              className={`p-2 rounded-lg transition-colors cosmic-hover-effect ${selectedMedia.length > 0 || selectedGif ? 'text-gray-500 cursor-not-allowed' : 'text-cyan-300 hover:text-cyan-200 hover:bg-emerald-800/20'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </button>

            {/* Bouton Programmer */}
            <button
              type="button"
              onClick={handleOpenSchedulePost}
              className={`p-2 rounded-lg transition-colors cosmic-hover-effect ${scheduledDate ? 'text-cyan-400 bg-cyan-900/20' : 'text-cyan-300 hover:text-cyan-200 hover:bg-emerald-800/20'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>

            {/* Bouton Thread */}
            <button
              type="button"
              onClick={isThreadMode ? disableThreadMode : enableThreadMode}
              className={`p-2 rounded-lg transition-colors cosmic-hover-effect ${
                isThreadMode 
                  ? 'text-cyan-400 bg-cyan-900/20' 
                  : 'text-cyan-300 hover:text-cyan-200 hover:bg-emerald-800/20'
              }`}
              title={isThreadMode ? 'Désactiver le mode thread' : 'Créer un thread (post long)'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
          </div>

          <div className="flex items-center space-x-3">
            {isExpanded && (
              <span className="text-sm text-emerald-300/80">
                {isThreadMode ? `Thread ${threadParts.length + 1} parties` :
                 selectedGif ? 'GIF' : 
                 poll ? 'Sondage' : 
                 `${selectedMedia.length}/${selectedMedia.some(m => m.type === 'video') ? '1 vidéo' : '4'}`}
              </span>
            )}
            
            <button
              type="submit"
              disabled={isPosting || !hasContent}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                hasContent && !isPosting
                  ? 'bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white shadow-lg'
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isPosting ? 'Publication...' : scheduledDate ? 'Programmer' : 'Publier'}
            </button>
          </div>
        </div>
      </form>

      {/* Modales */}
      <GifPicker
        isOpen={isGifPickerOpen}
        onClose={() => setIsGifPickerOpen(false)}
        onSelectGif={handleGifSelect}
      />
      
      <SchedulePost
        isOpen={isSchedulePostOpen}
        onClose={() => setIsSchedulePostOpen(false)}
        onSchedule={handleSchedule}
      />
      
      {/* Sélecteur de mentions */}
      <MentionSelector
        isOpen={showMentionSelector}
        searchQuery={mentionQuery}
        position={mentionPosition}
        onSelectMention={handleMentionSelect}
        onClose={() => setShowMentionSelector(false)}
      />
    </div>
  );
};

export default CreatePost;
